// shared/presigned-upload.ts — resilient browser → S3 presigned PUT
//
// Every document upload has two legs: POST /v2/upload (register the document and
// get a presigned URL), then PUT the file bytes straight from the browser to S3.
// Leg two never touches our servers, and it is by far the most failure-prone hop
// in the product — field agents upload over mobile data, and until this module
// existed a single dropped connection lost the whole upload with no retry.
//
// Prod measurement (2026-09-03 → 09-10): of 2,873 presigned URLs issued, 44
// (1.5%) never produced an S3 object. PDFs failed at 2.3% vs 1.0% for JPGs,
// because the PDFs people scan are ~20x larger at the median (1.02 MB vs
// 0.05 MB) and a longer transfer is more exposed to a drop. Failures clustered
// on individual mobile IPs while everyone else uploaded fine, so the cause is
// the client's connection — which is exactly what a retry is for.
//
// Two deliberate choices:
//
// 1. A bare axios instance, NOT the app's shared `axiosInstance`. The shared one
//    attaches `x-access-token` to every request, which meant every upload sent
//    the user's bearer token to S3 — which ignores it, since it is not part of
//    the presigned signature. Sending a credential somewhere it is neither
//    needed nor checked is worth avoiding on its own, and it also keeps the
//    preflight's Access-Control-Request-Headers list minimal.
//
//    Note this does NOT remove the preflight: a cross-origin PUT is never a
//    "simple" request, so the browser preflights every upload regardless of
//    headers. The bucket currently returns Access-Control-Max-Age: 0, so that
//    OPTIONS cannot be cached either — raising it is a separate CDK change in
//    KYCAndDocumentServiceCDK.
//
// 2. Bounded retry, but only for failures that are plausibly transient. A 4xx
//    from S3 means a bad or expired signature; retrying cannot succeed and just
//    burns the user's mobile data.

import axios, { AxiosError } from 'axios';

/** Total attempts, including the first. */
export const MAX_UPLOAD_ATTEMPTS = 3;

/** Per-attempt ceiling. Without this axios waits indefinitely on a stalled socket. */
const ATTEMPT_TIMEOUT_MS = 90_000;

/** Backoff before attempt 2 and attempt 3. */
const RETRY_DELAY_MS = [1_000, 3_000];

// Worst case wall-clock is 3 * 90s + 1s + 3s ≈ 4.6 minutes, comfortably inside
// the 15-minute presigned URL expiry (KYCD AmazonS3Accessor.EXPIRATION_SECONDS).
// If either constant grows, re-check that arithmetic — a retry that starts after
// the URL expires just produces a confusing 403.

/** No interceptors: no auth header, therefore no CORS preflight. */
const s3Client = axios.create();

export class PresignedUploadError extends Error {
  /** HTTP status, when a response actually came back. */
  readonly status?: number;
  /** How many attempts were made before giving up. */
  readonly attempts: number;
  /** True when the request never got a response at all (dropped connection). */
  readonly isNetworkFailure: boolean;

  constructor(
    message: string,
    details: { status?: number; attempts: number; isNetworkFailure: boolean },
  ) {
    super(message);
    this.name = 'PresignedUploadError';
    this.status = details.status;
    this.attempts = details.attempts;
    this.isNetworkFailure = details.isNetworkFailure;
  }
}

export interface PresignedUploadOptions {
  /** Progress percentage for the current attempt; resets to 0 on each retry. */
  onProgress?: (percent: number) => void;
  /** Fired before a retry, with the 1-based number of the attempt about to run. */
  onRetry?: (attempt: number, maxAttempts: number) => void;
}

/** A response we never got, or one S3 itself could not serve, is worth retrying.
 *  Anything else (403 expired signature, 400 malformed request) is terminal. */
function isRetryable(error: AxiosError): boolean {
  if (!error.response) return true; // dropped connection / timeout / CORS-blocked
  const { status } = error.response;
  return status === 429 || status >= 500;
}

function messageFor(error: AxiosError, attempts: number): string {
  if (!error.response) {
    return attempts > 1
      ? `Upload interrupted after ${attempts} attempts — check your connection and try again.`
      : 'Upload interrupted — check your connection and try again.';
  }
  const { status } = error.response;
  if (status === 403) {
    return 'Upload link expired before the file finished sending. Please try again.';
  }
  if (status === 413) {
    return 'This file is too large to upload.';
  }
  return `Upload rejected by storage (status ${status}).`;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * PUT file bytes to a presigned S3 URL, retrying transient failures.
 *
 * Throws {@link PresignedUploadError} whose `message` is already phrased for the
 * user — call sites can surface it directly in a toast.
 */
export async function putFileToPresignedUrl(
  file: File | Blob,
  presignedUrl: string,
  options: PresignedUploadOptions = {},
): Promise<void> {
  if (!presignedUrl) {
    throw new PresignedUploadError('Upload URL was not returned by the server.', {
      attempts: 0,
      isNetworkFailure: false,
    });
  }

  const { onProgress, onRetry } = options;
  let lastError: AxiosError | undefined;
  let attemptsMade = 0;

  for (let attempt = 1; attempt <= MAX_UPLOAD_ATTEMPTS; attempt++) {
    if (attempt > 1) {
      await delay(RETRY_DELAY_MS[attempt - 2] ?? 3_000);
      onRetry?.(attempt, MAX_UPLOAD_ATTEMPTS);
      onProgress?.(0); // the previous attempt's bytes are gone; start over
    }

    attemptsMade = attempt;

    try {
      await s3Client.put(presignedUrl, file, {
        headers: { 'Content-Type': 'application/octet-stream' },
        timeout: ATTEMPT_TIMEOUT_MS,
        onUploadProgress: (event) => {
          if (event.total && onProgress) {
            onProgress(Math.round((event.loaded * 100) / event.total));
          }
        },
      });
      return;
    } catch (error) {
      const axiosError = error as AxiosError;
      lastError = axiosError;
      if (!isRetryable(axiosError)) break; // terminal: don't burn more data
    }
  }

  const error = lastError as AxiosError;
  throw new PresignedUploadError(messageFor(error, attemptsMade), {
    status: error?.response?.status,
    attempts: attemptsMade,
    isNetworkFailure: !error?.response,
  });
}
